// Use empty string to leverage Vercel proxy to Railway backend
const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export interface AdminLoginRequest {
  email: string
  password: string
}

export interface AdminLoginResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface MediaUploadRequest {
  filename: string
  contentType: string
}

export interface PresignedUrlResponse {
  mediaId: string
  uploadUrl: string
  objectKey: string
  expiresAt: string
  maxFileSizeBytes: number
  method: string
  headers?: Record<string, string>
  message?: string
}

export interface MediaUploadConfirmRequest {
  mediaId: string
  etag: string
  fileSizeBytes: number
}

export interface MediaUploadResponse {
  mediaId: string
  filename: string | null
  contentType: string | null
  objectKey: string | null
  status: 'PENDING' | 'COMPLETED' | 'FAILED'
  uploadedAt: string | null
  fileSizeBytes: number
  message?: string
}

export interface JerseyCreateRequest {
  playerName: string
  jerseyNumber: number
  team: string
  size: string
  condition: string
  gameTitle?: string
  gameDate?: string
  opponent?: string
  venue?: string
  stats?: {
    touchdowns?: number
    passingYards?: number
    completions?: number
  }
  description?: string
  mediaIds?: string[]
}

export interface NfcTag {
  id: number
  uid: string
  displayUid: string
  claimCode?: string
  status: 'READY' | 'BOUND' | 'ACTIVATED' | 'LOST' | 'REVOKED'
  availableForAssignment: boolean
  assignedListingId?: string
  assignedListingTitle?: string
  createdAt: string
  updatedAt: string
}

export interface NfcBindRequest {
  uid?: string
  claimCode?: string
}

export interface NfcOperationResponse {
  success: boolean
  message: string
  operationType: 'BIND' | 'PUBLISH' | 'PROVISION'
  tag?: NfcTag
  listingId?: string
  listingTitle?: string
  adminUserId: string
  errors?: string[]
  timestamp: string
}

export interface NfcQrResponse {
  qrCodeUrl: string
  bindingUrl: string
  expiresAt: string
}

class AdminAPI {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken')
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  }

  async login(credentials: AdminLoginRequest): Promise<AdminLoginResponse> {
    const response = await fetch(`${API_BASE_URL}/api/admin/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(credentials)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Admin login failed')
    }

    return response.json()
  }

  async logout(): Promise<void> {
    const token = localStorage.getItem('authToken')
    if (!token) return

    try {
      await fetch(`${API_BASE_URL}/api/admin/auth/logout`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      })
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('authToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
    }
  }

  async createJersey(data: JerseyCreateRequest): Promise<{ message: string; id: string }> {
    const response = await fetch(`${API_BASE_URL}/api/admin/jerseys`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create jersey')
    }

    return response.json()
  }

  async getAnalytics(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/admin/analytics`, {
      headers: this.getAuthHeaders()
    })

    if (!response.ok) {
      throw new Error('Failed to fetch analytics')
    }

    return response.json()
  }

  async uploadBulkData(file: File): Promise<{ message: string; processed: number; errors: string[] }> {
    const formData = new FormData()
    formData.append('file', file)

    const token = localStorage.getItem('authToken')
    const response = await fetch(`${API_BASE_URL}/api/admin/bulk-import`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: formData
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Bulk import failed')
    }

    return response.json()
  }

  // Media Upload Methods
  async getPresignedUploadUrl(request: MediaUploadRequest): Promise<PresignedUrlResponse> {
    const response = await fetch(`${API_BASE_URL}/api/admin/media/upload/presigned-url`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to get upload URL')
    }

    return response.json()
  }

  async uploadToPresignedUrl(
    presignedUrl: string, 
    file: File, 
    headers?: Record<string, string>
  ): Promise<{ etag: string }> {
    const uploadHeaders: HeadersInit = {
      'Content-Type': file.type,
      ...headers
    }

    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: uploadHeaders,
      body: file
    })

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`)
    }

    const etag = response.headers.get('etag')?.replace(/"/g, '') || ''
    return { etag }
  }

  async confirmUpload(request: MediaUploadConfirmRequest): Promise<MediaUploadResponse> {
    const response = await fetch(`${API_BASE_URL}/api/admin/media/upload/confirm`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to confirm upload')
    }

    return response.json()
  }

  async getUploadStatus(mediaId: string): Promise<MediaUploadResponse> {
    const response = await fetch(`${API_BASE_URL}/api/admin/media/upload/status/${mediaId}`, {
      headers: this.getAuthHeaders()
    })

    if (!response.ok) {
      throw new Error('Failed to get upload status')
    }

    return response.json()
  }

  // Complete media upload flow
  async uploadMedia(file: File): Promise<MediaUploadResponse> {
    // Step 1: Get presigned URL
    const presignedResponse = await this.getPresignedUploadUrl({
      filename: file.name,
      contentType: file.type
    })

    // Step 2: Upload to S3
    const { etag } = await this.uploadToPresignedUrl(
      presignedResponse.uploadUrl,
      file,
      presignedResponse.headers
    )

    // Step 3: Confirm upload
    const confirmResponse = await this.confirmUpload({
      mediaId: presignedResponse.mediaId,
      etag,
      fileSizeBytes: file.size
    })

    return confirmResponse
  }

  // ================================
  // NFC MANAGEMENT METHODS
  // ================================

  async getAvailableNfcTags(): Promise<NfcTag[]> {
    const response = await fetch(`${API_BASE_URL}/api/admin/nfc/tags/available`, {
      headers: this.getAuthHeaders()
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch available NFC tags')
    }

    return response.json()
  }

  async searchNfcTags(params?: {
    status?: 'READY' | 'BOUND' | 'ACTIVATED' | 'LOST' | 'REVOKED'
    search?: string
    page?: number
    size?: number
  }): Promise<{ content: NfcTag[], totalElements: number, totalPages: number }> {
    const searchParams = new URLSearchParams()
    
    if (params?.status) searchParams.append('status', params.status)
    if (params?.search) searchParams.append('search', params.search)
    if (params?.page !== undefined) searchParams.append('page', params.page.toString())
    if (params?.size !== undefined) searchParams.append('size', params.size.toString())

    const response = await fetch(`${API_BASE_URL}/api/admin/nfc/tags?${searchParams}`, {
      headers: this.getAuthHeaders()
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to search NFC tags')
    }

    return response.json()
  }

  async getNfcTagByUid(uid: string): Promise<NfcTag> {
    const response = await fetch(`${API_BASE_URL}/api/admin/nfc/tags/${encodeURIComponent(uid)}`, {
      headers: this.getAuthHeaders()
    })

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('NFC tag not found')
      }
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch NFC tag')
    }

    return response.json()
  }

  async bindNfcToListing(listingId: string, request: NfcBindRequest): Promise<NfcOperationResponse> {
    const response = await fetch(`${API_BASE_URL}/api/admin/listings/${listingId}/bind`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to bind NFC tag to listing')
    }

    return response.json()
  }

  async publishListing(listingId: string): Promise<NfcOperationResponse> {
    const response = await fetch(`${API_BASE_URL}/api/admin/listings/${listingId}/publish`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to publish listing')
    }

    return response.json()
  }

  async generateNfcBindingQr(listingId: string): Promise<NfcQrResponse> {
    const response = await fetch(`${API_BASE_URL}/api/admin/listings/${listingId}/nfc/qr`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to generate NFC binding QR code')
    }

    return response.json()
  }

  async provisionNfcTag(uid: string, claimCode?: string): Promise<NfcOperationResponse> {
    const response = await fetch(`${API_BASE_URL}/api/admin/nfc/tags/provision`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        uid,
        claimCode: claimCode || undefined
      })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to provision NFC tag')
    }

    return response.json()
  }
}

export const adminAPI = new AdminAPI()